// Apple-Inspired Loading Indicator Component
// Displays loading states with elegant design and user feedback

import React from 'react';
import { LoadingState } from '../../services/loadingManager';
import './LoadingIndicator.css';

interface LoadingIndicatorProps {
  loading?: boolean;
  loadingState?: LoadingState | null;
  message?: string;
  size?: 'small' | 'medium' | 'large';
  showProgress?: boolean;
  className?: string;
}

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'card' | 'table' | 'button' | 'avatar';
  lines?: number;
  width?: string | number;
  height?: string | number;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  loading = false,
  loadingState = null,
  message,
  size = 'medium',
  showProgress = false,
  className = ''
}) => {
  // Determine if we should show loading
  const isLoading = loading || (loadingState?.isLoading ?? false);
  
  if (!isLoading) {
    return null;
  }

  // Get display message
  const displayMessage = message || loadingState?.message || 'Loading...';
  
  // Get progress value
  const progress = loadingState?.progress;
  const showProgressBar = showProgress && progress !== undefined;

  // Calculate estimated time remaining
  const getTimeRemaining = (): string | null => {
    if (!loadingState?.startTime || !loadingState?.estimatedDuration) {
      return null;
    }

    const elapsed = Date.now() - loadingState.startTime;
    const remaining = Math.max(0, loadingState.estimatedDuration - elapsed);
    
    if (remaining < 1000) {
      return 'Almost done...';
    } else if (remaining < 60000) {
      return `${Math.ceil(remaining / 1000)}s remaining`;
    } else {
      return `${Math.ceil(remaining / 60000)}m remaining`;
    }
  };

  const timeRemaining = getTimeRemaining();

  return (
    <div className={`loading-indicator ${size} ${className}`}>
      <div className="loading-content">
        {/* Apple-inspired Spinner */}
        <div className="loading-spinner">
          <div className="spinner" role="status" aria-label="Loading"></div>
        </div>

        {/* Message */}
        <div className="loading-message">
          <div className="primary-message">{displayMessage}</div>
          {timeRemaining && (
            <div className="time-remaining">{timeRemaining}</div>
          )}
        </div>

        {/* Progress Bar */}
        {showProgressBar && (
          <div className="loading-progress">
            <div className="progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
              <div 
                className="progress-fill"
                style={{ width: `${Math.min(Math.max(progress!, 0), 100)}%` }}
              />
            </div>
            <div className="progress-text">
              {Math.round(progress!)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Skeleton Loading Component for Apple-inspired loading patterns
export const SkeletonLoader: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  lines = 3,
  width,
  height
}) => {
  const getSkeletonStyle = () => {
    const style: React.CSSProperties = {};
    if (width) style.width = typeof width === 'number' ? `${width}px` : width;
    if (height) style.height = typeof height === 'number' ? `${height}px` : height;
    return style;
  };

  switch (variant) {
    case 'text':
      return (
        <div className={`skeleton-container ${className}`}>
          {Array.from({ length: lines }, (_, index) => (
            <div
              key={index}
              className="skeleton-loader skeleton-text"
              style={getSkeletonStyle()}
              aria-hidden="true"
            />
          ))}
        </div>
      );

    case 'card':
      return (
        <div className={`skeleton-loader skeleton-card ${className}`} style={getSkeletonStyle()} aria-hidden="true">
          <div className="skeleton-loader skeleton-text large" style={{ marginBottom: 'var(--space-4)' }} />
          <div className="skeleton-loader skeleton-text" />
          <div className="skeleton-loader skeleton-text" />
          <div className="skeleton-loader skeleton-text" style={{ width: '60%' }} />
        </div>
      );

    case 'table':
      return (
        <div className={`skeleton-container ${className}`}>
          {Array.from({ length: lines }, (_, index) => (
            <div key={index} className="skeleton-table-row">
              <div className="skeleton-loader skeleton-table-cell" />
              <div className="skeleton-loader skeleton-table-cell" />
              <div className="skeleton-loader skeleton-table-cell" />
              <div className="skeleton-loader skeleton-table-cell" style={{ width: '60%' }} />
            </div>
          ))}
        </div>
      );

    case 'button':
      return (
        <div
          className={`skeleton-loader skeleton-button ${className}`}
          style={getSkeletonStyle()}
          aria-hidden="true"
        />
      );

    case 'avatar':
      return (
        <div
          className={`skeleton-loader skeleton-avatar ${className}`}
          style={getSkeletonStyle()}
          aria-hidden="true"
        />
      );

    default:
      return (
        <div
          className={`skeleton-loader ${className}`}
          style={getSkeletonStyle()}
          aria-hidden="true"
        />
      );
  }
};

// Overlay version for full-screen loading with Apple-inspired design
export const LoadingOverlay: React.FC<LoadingIndicatorProps> = (props) => {
  const isLoading = props.loading || (props.loadingState?.isLoading ?? false);
  
  if (!isLoading) {
    return null;
  }

  return (
    <div className="loading-overlay" role="dialog" aria-modal="true" aria-label="Loading">
      <div className="loading-overlay-content">
        <LoadingIndicator {...props} size="large" />
      </div>
    </div>
  );
};

// Inline version for buttons and small components with Apple styling
export const InlineLoader: React.FC<{ 
  loading?: boolean; 
  size?: 'small' | 'medium';
  className?: string;
}> = ({ 
  loading = false, 
  size = 'small',
  className = ''
}) => {
  if (!loading) {
    return null;
  }

  return (
    <div className={`inline-loader ${size} ${className}`}>
      <div className="spinner" role="status" aria-label="Loading"></div>
    </div>
  );
};

export default LoadingIndicator;